import {
  AlignmentMeasurementTool,
  AngleMeasurement,
  AngleMeasurementTool,
  Markup,
  MarkupEventName,
  MarkupPlugin,
  Measurement as Web3DLineMeasurement,
  MeasurementTool,
  ImagePosition,
  PointMarkupTool,
  PolylineMeasurementTool,
  PolylineMeasurement,
  PolygonMeasurementTool,
  PolygonMeasurement,
} from '@technology/web3d-plugin-markup';
import { Color, Web3DViewer, SnapPick, Vector3 } from '@technology/web3d';
import {
  AngleFormatter,
  detectArrayChanges,
  Web3DViewerWrapper,
  calculateDistance,
  partialPointToVector3,
  LengthFormatter,
  chainDistance,
  rgbToWeb3dColor,
  formatChainageLabel,
  geoValuesEqual,
  RGBFormat,
} from '@trimble/utilities';
import {
  Accessor,
  createEffect,
  createMemo,
  createSignal,
  on,
  Setter,
} from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';
import { SnapType } from '../snapping/snap-type';
import {
  AngleMeasurementType,
  Measurement,
  MeasurementType,
  PointMeasurementType,
  PointMeasurementSettings,
  LineMeasurementType,
  PolylineMeasurementType,
  SlopeFormat,
  MeasurementSaveStatus,
  AreaMeasurementType,
  DEFAULT_POLYGON_MEASUREMENT_SETTINGS,
  VolumeMeasurementType,
} from './measurement';
import { SharedCapabilityProviderBase } from '../base/shared-provider-base';
import { MeasurementStyleManager } from './measurement-style-manager';
import {
  convertMarkupToMeasurement,
  createAngleMeasurement,
  transformTcMarkupToMeasurement,
  MeasurementMarkup,
  processMarkup,
  pointPositionFormatter,
  getLabelFromMarkup,
  slopeFormatter,
  clonePicks,
  boundaryInclusiveToSnapPicks,
  VOLUME_MEASUREMENT_TILESET_MODEL_ID_PREFIX,
  getBoundaryPointsFromPolygonMarkup,
} from './measurement-helpers';
import { Markup as ConnectMarkup } from '@data-access/connect/tc-tcps';
import { Web3DCustomEvent } from '../design-package/design-package-interfaces';
import { AngleUnit, AreaUnit, LengthUnit } from '@fulcrum/lib';
import { AlignmentSettings, FormattingSettings } from '@trimble/types';
import { getLogger } from '@trimble/logging';

/** The base unit for length measurements. */
const BASE_LENGTH_UNIT = 'm';
const BASE_ANGLE_UNIT = 'rad';
const BASE_AREA_UNIT = 'm2';
const CUSTOM_LENGTH_UNIT = 'custom';

/* eslint-disable no-magic-numbers -- Default magenta color RGB values (236, 0, 182) */
const RGB_FACTOR = 255;
const DEFAULT_MAGENTA_COLOR = new Color(
  236 / RGB_FACTOR,
  0 / RGB_FACTOR,
  182 / RGB_FACTOR
);
/* eslint-enable no-magic-numbers */

const SELECTED_MARKUP_RENDER_ORDER = 1;
const DEFAULT_MARKUP_RENDER_ORDER = 0;

export class MeasurementsCapabilityProvider extends SharedCapabilityProviderBase<Measurement> {
  public idKey = 'id' as const;
  private readonly viewerEventListeners = new Map<
    Web3DViewer,
    Array<{ event: string; handler: EventListener }>
  >();
  private readonly styleManager = new MeasurementStyleManager();

  /**
   * When true, markup click/selection events are ignored and will not change the
   * selected measurement. Set this while a panel (e.g. volumetric create) needs
   * to intercept markup picks without triggering measurement navigation.
   */
  private isMarkupSelectionSuppressedFlag = false;

  public isMarkupSelectionSuppressed(): boolean {
    return this.isMarkupSelectionSuppressedFlag;
  }

  public setMarkupSelectionSuppressed(value: boolean): void {
    this.isMarkupSelectionSuppressedFlag = value;
  }

  /**
   * When set, the volume measurement tileset with this ID will not be rendered.
   * Used during volume edit/recompute to hide the stale heatmap overlay.
   */
  public readonly tilesetDisplaySuppressedMeasurementId: Accessor<
    string | undefined
  >;
  private readonly setTilesetDisplaySuppressedMeasurementIdInternal: Setter<
    string | undefined
  >;

  public setTilesetDisplaySuppressedMeasurementId(
    id: string | undefined
  ): void {
    this.setTilesetDisplaySuppressedMeasurementIdInternal(id);
  }

  public readonly lengthUnit: LengthUnit = BASE_LENGTH_UNIT;
  public readonly angleUnit: AngleUnit = BASE_ANGLE_UNIT;
  public readonly areaUnit: AreaUnit = BASE_AREA_UNIT;
  public readonly formattingSettings: Accessor<FormattingSettings>;
  public readonly enableMeasurementSelection: Accessor<boolean>;
  public readonly selectedMeasurement: Accessor<Measurement | undefined>;
  public readonly pointMeasurementSettings: Accessor<PointMeasurementSettings>;
  public readonly setPointMeasurementSettings: Setter<PointMeasurementSettings>;
  public readonly slopeFormat: Accessor<SlopeFormat>;
  public readonly setSlopeFormat: Setter<SlopeFormat>;
  private readonly alignmentSettings: Accessor<AlignmentSettings | undefined>;

  public cleanup(): Promise<void> {
    this.clearMarkupSelection();
    this.removeAllMeasurements();
    this.removeAllEventListeners();
    return Promise.resolve(undefined);
  }

  constructor(
    formattingSettings: () => FormattingSettings,
    enableMeasurementSelection?: () => boolean,
    alignmentSettings?: () => AlignmentSettings | undefined
  ) {
    super();

    const [
      tilesetDisplaySuppressedMeasurementId,
      setTilesetDisplaySuppressedMeasurementIdInternal,
    ] = createSignal<string | undefined>();
    this.tilesetDisplaySuppressedMeasurementId =
      tilesetDisplaySuppressedMeasurementId;
    this.setTilesetDisplaySuppressedMeasurementIdInternal =
      setTilesetDisplaySuppressedMeasurementIdInternal;

    this.formattingSettings = formattingSettings;
    this.alignmentSettings = alignmentSettings ?? (() => undefined);
    this.enableMeasurementSelection =
      enableMeasurementSelection ?? (() => true);

    const [pointMeasurementSettings, setPointMeasurementSettings] =
      createSignal<PointMeasurementSettings>({
        showXYCoordinates: true,
        showZCoordinate: true,
      });

    this.pointMeasurementSettings = pointMeasurementSettings;
    this.setPointMeasurementSettings = setPointMeasurementSettings;

    const [slopeFormat, setSlopeFormat] = createSignal<SlopeFormat>('ratio');
    this.slopeFormat = slopeFormat;
    this.setSlopeFormat = setSlopeFormat;

    // Create memo for selected measurement
    this.selectedMeasurement = createMemo(
      () => this.selectedItem() ?? undefined
    );

    // Sync formatting settings.
    createEffect(
      on(
        [this.viewerWrappers, formattingSettings],
        ([viewerWrappers, formattingSettings], prev) => {
          const [prevViewerWrappers, prevFormattingSettings] = prev ?? [
            undefined,
            undefined,
          ];

          const { newItems } = detectArrayChanges(
            prevViewerWrappers,
            viewerWrappers
          );

          if (newItems.length > 0) {
            // Apply settings to new viewers only
            this.applyFormattingSettingsToViewers(formattingSettings, newItems);
          } else if (prevFormattingSettings !== formattingSettings) {
            // Apply settings to all viewers when length unit or decimal places change
            this.applyFormattingSettingsToViewers(
              formattingSettings,
              viewerWrappers
            );
          }

          // Re-apply chainage formatting to alignment measurements after the
          // viewer's customLengthFormatter has been (re-)set, because the web3d
          // library synchronously re-formats all labels with the generic formatter
          // when customLengthFormatter changes.
          this.applyChainageFormatterToAlignmentMeasurements();
        }
      )
    );

    // Re-apply chainage formatting when alignment settings change.
    if (alignmentSettings) {
      createEffect(
        on(alignmentSettings, () => {
          this.applyChainageFormatterToAlignmentMeasurements();
        })
      );
    }

    // Sync markup selection.
    createEffect(
      on(
        [this.selectedMeasurement, this.enableMeasurementSelection],
        ([measurement, enableMeasurementSelection]) => {
          // This is configured to disable measurement selection by blocking
          // this select action. This was needed to support Tiering implementation
          // @see https://trimble-arch.aha.io/features/WM-648
          // ToDo: Improve this disable logic to use a standardised pattern for
          //  Tiering, project roles, feature flagging and reduced linearity
          this.resetAllMarkupsRenderOrder();

          if (!measurement || !enableMeasurementSelection) {
            this.clearMarkupSelection();
            return;
          }

          for (const viewerWrapper of this.viewerWrappers()) {
            const markups = this.getViewerMarkups(
              viewerWrapper.viewer,
              measurement
            );
            if (markups.length === 0) continue;

            // Sometimes Web3D dependency would crash here and program flow would be
            // broken - side panel for measurements properties wouldn't open. So handling
            // it with try/catch.
            try {
              viewerWrapper.viewer.selection.set(
                'markup',
                markups.map((m) => m.id)
              );
            } catch (error) {
              getLogger().warn('Failed to set markup selection', error);
            }
            this.bringMarkupsToFront(markups);
          }
        }
      )
    );

    // Sync markup deletion.
    createEffect(
      on(this.items, (current, prev) => {
        if (prev == undefined) {
          return;
        }
        const deletedMeasurements = prev.filter(
          (prevItem) =>
            // Check if measurements has some markups in common (so it's same markup).
            !current.some((currItem) =>
              this.getValidMarkups(currItem.markups).some((ms) =>
                this.getValidMarkups(prevItem.markups).some(
                  (m) => m.id == ms.id
                )
              )
            )
        );

        deletedMeasurements.forEach((item) => {
          const ids = this.getValidMarkups(item.markups).map((m) => m.id);
          for (const viewerWrapper of this.viewerWrappers()) {
            const markupPlugin = viewerWrapper.viewer.plugins.get(MarkupPlugin);
            if (!markupPlugin) {
              throw new Error('MarkupPlugin is not available on the viewer');
            }

            const markupIds = markupPlugin
              .getMarkups()
              .filter((m) => ids.some((id) => id === m.id))
              .map((m) => m.id);

            if (markupIds.length) {
              markupPlugin.removeById(markupIds);
            }
          }
        });

        return [...current];
      })
    );

    // Sync from viewer to side panel.
    createEffect(
      on(this.viewerWrappers, (viewerWrappers, previousViewerWrappers) => {
        const { newItems: newViewerWrappers, removedItems: removedViewers } =
          detectArrayChanges(previousViewerWrappers, viewerWrappers);

        // Remove event listeners from removed viewers
        removedViewers.forEach((viewer) => {
          this.removeEventListenersForViewer(viewer);
        });

        if (!newViewerWrappers.length) {
          return;
        }

        for (const viewerWrapper of newViewerWrappers) {
          this.addEventListenersToViewer(viewerWrapper, previousViewerWrappers);
        }
      })
    );
  }

  private applyFormattingSettingsToViewers(
    formattingSettings: FormattingSettings,
    viewerWrappers: Web3DViewerWrapper[]
  ): void {
    for (const viewerWrapper of viewerWrappers) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      viewerWrapper.viewer.settings.lengthUnit = CUSTOM_LENGTH_UNIT as any;

      viewerWrapper.viewer.settings.customLengthFormatter = (
        lengthInMeters: number
      ): string =>
        LengthFormatter.toDisplay({
          measuredValue: lengthInMeters,
          currentUnits: 'm',
          settings: formattingSettings,
          useMeasurementSettings: true,
        });

      viewerWrapper.viewer.settings.customAngleFormatter = (
        angleInRadians: number
      ): string =>
        AngleFormatter.toDisplay({
          measuredValue: angleInRadians,
          currentUnits: 'rad',
          settings: formattingSettings,
        });
    }
  }

  /**
   * Applies chainage label formatting to the distance label of all alignment
   * measurement markups.  The web3d AlignmentMeasurementTool internally uses
   * the viewer's `customLengthFormatter` for the station label, which produces
   * a plain length string.  This method overrides the formatter on each
   * distance label so that the value is displayed using the alignment label
   * format (e.g. "01+50.0" instead of "150.00 m").
   */
  private applyChainageFormatterToAlignmentMeasurements(): void {
    const settings = this.alignmentSettings();
    const fmtSettings = this.formattingSettings();

    for (const measurement of this.items()) {
      if (measurement.type !== 'alignment') continue;

      for (const markup of this.getValidMarkups(measurement.markups)) {
        if (settings) {
          this.applyChainageFormatterToMarkup(markup, settings, fmtSettings);
        } else {
          this.removeChainageFormatterFromMarkup(markup, fmtSettings);
        }
      }
    }
  }

  /**
   * Overrides the internal distance label formatter on an AlignmentMeasurement
   * markup so that the station/chainage value is displayed with the alignment
   * label format instead of the generic length format.
   */
  private applyChainageFormatterToMarkup(
    markup: Markup,
    settings: AlignmentSettings,
    fmtSettings: FormattingSettings
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Accessing internal _distanceLabel; the web3d library does not expose a public API for per-label formatting
    const distanceLabel = (markup as any)._distanceLabel;
    if (!distanceLabel) return;

    const displayUnit = (fmtSettings.lengthUnit ?? 'm') as LengthUnit;
    const decimals = fmtSettings.lengthDecimals;

    const chainageFormatter = (valueInMeters: number): string =>
      formatChainageLabel(
        valueInMeters,
        settings.labelFormat,
        displayUnit,
        decimals
      );

    distanceLabel._formatter = chainageFormatter;
    distanceLabel.text = chainageFormatter(distanceLabel._unformattedValue);
    distanceLabel.updateTexture();

    // Monkey-patch updateText (only once) so that when the web3d library
    // re-formats labels on settings changes, the chainage formatter is
    // re-applied instead of the generic length formatter.
    if (!distanceLabel._originalUpdateText) {
      distanceLabel._originalUpdateText =
        distanceLabel.updateText.bind(distanceLabel);
      distanceLabel.updateText = function (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- internal options type from web3d
        options: any
      ): void {
        this._originalUpdateText(options);
        if (this._chainageFormatter) {
          this._formatter = this._chainageFormatter;
          this.text = this._chainageFormatter(this._unformattedValue);
        }
      };
    }
    distanceLabel._chainageFormatter = chainageFormatter;
  }

  /**
   * Removes the chainage formatter from a markup's distance label and reverts
   * it to the standard length formatter so the label displays a plain length
   * string (e.g. "150.00 m") again.
   */
  private removeChainageFormatterFromMarkup(
    markup: Markup,
    fmtSettings: FormattingSettings
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Accessing internal _distanceLabel; the web3d library does not expose a public API for per-label formatting
    const distanceLabel = (markup as any)._distanceLabel;
    if (!distanceLabel) return;

    distanceLabel._chainageFormatter = null;

    const standardFormatter = (valueInMeters: number): string =>
      LengthFormatter.toDisplay({
        measuredValue: valueInMeters,
        currentUnits: 'm',
        settings: fmtSettings,
        useMeasurementSettings: false,
      });

    distanceLabel._formatter = standardFormatter;
    distanceLabel.text = standardFormatter(distanceLabel._unformattedValue);
    distanceLabel.updateTexture();
  }

  // During initialization markups can be undefined, so we are filtering that case
  private getValidMarkups(markups: Markup[]): Markup[] {
    return markups.filter((m): m is Markup => m != null);
  }

  private findMeasurementForMarkup(markup: Markup): Measurement | undefined {
    return this.items().find((measurement) =>
      this.getValidMarkups(measurement.markups).some((m) => markup.id === m.id)
    );
  }

  /**
   * Checks if a measurement exists for the given markup and updates it if changed.
   *
   * @param markup - A new or modified markup object.
   * @returns true if an existing measurement was found and updated, false otherwise.
   */
  private updateMeasurementForMarkup(markup: Markup): boolean {
    const measurement = this.findMeasurementForMarkup(markup);
    if (measurement) {
      switch (measurement.type) {
        case 'point':
          this.syncPointMeasurement(measurement, markup);
          break;
        case 'line':
          this.syncLineMeasurement(measurement, markup);
          break;
        case 'polyline':
          this.syncPolylineMeasurement(
            measurement,
            markup as PolylineMeasurement
          );
          break;
        case 'area':
          this.syncAreaMeasurement(measurement, markup as PolygonMeasurement);
          break;
        case 'angle':
          this.syncAngleMeasurement(measurement, markup);
          break;
        case 'alignment':
        case 'volume':
          break;
        default:
          throw new UnreachableCaseError(measurement);
      }
      return true;
    }
    return false;
  }

  private syncPointMeasurement(
    measurement: PointMeasurementType,
    markup: Markup
  ): void {
    if (
      measurement.x === markup.position.x &&
      measurement.y === markup.position.y &&
      measurement.z === markup.position.z
    ) {
      return;
    }

    const newMarkups = this.getValidMarkups(measurement.markups).map(
      (existingMarkup) => {
        if (existingMarkup.id !== markup.id) {
          existingMarkup.position.copy(markup.position);
        }
        return existingMarkup;
      }
    );
    const updatedMeasurement: PointMeasurementType = {
      ...measurement,
      x: markup.position.x,
      y: markup.position.y,
      z: markup.position.z,
      markups: newMarkups,
    };
    this.updateMeasurement(updatedMeasurement);
  }

  /**
   * Shared sync flow: detect change, find source viewer, build updated measurement,
   * update state, and re-create markup in other viewers. Parameterized by change
   * detection and a measurement-builder callback.
   *
   * NOTE — push-after-setItems pattern: this method calls `updateMeasurement`
   * (which calls `setItems`) with only the source viewer's markup, then
   * `createMeasurement` pushes replica markups onto `updatedMeasurement.markups`
   * afterward. Because the stored item shares the same array reference the
   * push is visible via `this.items()`, but it bypasses SolidJS reactivity —
   * no `createEffect` depending on `items()` will re-run for the markup count
   * change. The safer pattern (used in `markups-provider.ts`) is to collect
   * all replica markups first and call `setItems` once with the fully-populated
   * list. Refactoring this would also eliminate the microtask race window that
   * NUMARK-797 exposed.
   *
   * TODO: refactor to collect all replicas before calling `updateMeasurement`,
   * matching the approach in `MarkupsCapabilityProvider.syncMarkupWithViewerMarkup`.
   */
  private syncMeasurementWithMarkup<M extends Measurement>(
    measurement: M,
    markup: Markup,
    hasChanged: boolean,
    buildUpdated: (measurement: M, sourceViewer: Web3DViewer) => M,
    errorContext: string
  ): void {
    if (!hasChanged) {
      return;
    }

    const sourceWrapper = this.viewerWrappers().find((vw) =>
      this.getViewerMarkups(vw.viewer, measurement).some(
        (m) => m.id === markup.id
      )
    );
    if (!sourceWrapper) {
      getLogger().error(errorContext, {
        measurementId: measurement.id,
        markupId: markup.id,
      });
      return;
    }

    const sourceViewer = sourceWrapper.viewer;
    const updatedMeasurement = buildUpdated(measurement, sourceViewer);
    this.updateMeasurement(updatedMeasurement);

    for (const viewerWrapper of this.viewerWrappers()) {
      if (viewerWrapper.viewer === sourceViewer) {
        continue;
      }
      const existingMarkups = this.getViewerMarkups(
        viewerWrapper.viewer,
        measurement
      );
      viewerWrapper.markupPlugin.removeById(existingMarkups.map((m) => m.id));
      this.createMeasurement(updatedMeasurement, [viewerWrapper]);
    }
  }

  private syncLineMeasurement(
    measurement: LineMeasurementType,
    markup: Markup
  ): void {
    if (!(markup instanceof Web3DLineMeasurement)) {
      getLogger().warn('Line measurement markup is not a LineMarkup instance', {
        measurementId: measurement.id,
        markup,
      });
      return;
    }

    const mainLine = markup.getMainLine();
    const startPoint = mainLine.start.clone();
    const endPoint = mainLine.end.clone();
    const startChanged =
      !geoValuesEqual(measurement.startPoint.x, startPoint.x) ||
      !geoValuesEqual(measurement.startPoint.y, startPoint.y) ||
      !geoValuesEqual(measurement.startPoint.z, startPoint.z);
    const endChanged =
      !geoValuesEqual(measurement.endPoint.x, endPoint.x) ||
      !geoValuesEqual(measurement.endPoint.y, endPoint.y) ||
      !geoValuesEqual(measurement.endPoint.z, endPoint.z);

    this.syncMeasurementWithMarkup(
      measurement,
      markup,
      startChanged || endChanged,
      (m, sourceViewer) => ({
        ...m,
        startPoint,
        endPoint,
        value: calculateDistance(startPoint, endPoint),
        markups: this.getViewerMarkups(sourceViewer, m),
      }),
      'Line measurement markup not found in viewers'
    );
  }

  private syncPolylineMeasurement(
    measurement: PolylineMeasurementType,
    markup: PolylineMeasurement
  ): void {
    this.syncMeasurementWithMarkup(
      measurement,
      markup,
      this.hasPicksChanged(measurement.picks, markup.picks),
      (m, sourceViewer) => ({
        ...m,
        picks: clonePicks(markup.picks),
        value: chainDistance(markup.picks),
        markups: this.getViewerMarkups(sourceViewer, m),
      }),
      'Polyline measurement markup not found in viewers'
    );
  }

  private syncAreaMeasurement(
    measurement: AreaMeasurementType,
    markup: PolygonMeasurement
  ): void {
    this.syncMeasurementWithMarkup(
      measurement,
      markup,
      this.hasPicksChanged(measurement.picks, markup.picks),
      (m, sourceViewer) => ({
        ...m,
        picks: clonePicks(markup.picks),
        markups: this.getViewerMarkups(sourceViewer, m),
      }),
      'Area measurement markup not found in viewers'
    );
  }

  private syncAngleMeasurement(
    measurement: AngleMeasurementType,
    markup: Markup
  ): void {
    if (!(markup instanceof AngleMeasurement)) {
      getLogger().warn(
        'Angle measurement markup is not an AngleMeasurement instance',
        { measurementId: measurement.id, markup }
      );
      return;
    }

    let updatedMeasurement: AngleMeasurementType;
    try {
      updatedMeasurement = createAngleMeasurement(
        markup,
        measurement.label || ''
      ) as AngleMeasurementType;
    } catch (e) {
      getLogger().warn('Failed to create angle measurement for sync', {
        measurementId: measurement.id,
        markup,
        error: e,
      });
      return;
    }

    const startChanged =
      !geoValuesEqual(
        measurement.startPoint.x,
        updatedMeasurement.startPoint.x
      ) ||
      !geoValuesEqual(
        measurement.startPoint.y,
        updatedMeasurement.startPoint.y
      ) ||
      !geoValuesEqual(
        measurement.startPoint.z,
        updatedMeasurement.startPoint.z
      );
    const endChanged =
      !geoValuesEqual(measurement.endPoint.x, updatedMeasurement.endPoint.x) ||
      !geoValuesEqual(measurement.endPoint.y, updatedMeasurement.endPoint.y) ||
      !geoValuesEqual(measurement.endPoint.z, updatedMeasurement.endPoint.z);
    const middleChanged =
      !geoValuesEqual(
        measurement.middlePoint.x,
        updatedMeasurement.middlePoint.x
      ) ||
      !geoValuesEqual(
        measurement.middlePoint.y,
        updatedMeasurement.middlePoint.y
      ) ||
      !geoValuesEqual(
        measurement.middlePoint.z,
        updatedMeasurement.middlePoint.z
      );

    this.syncMeasurementWithMarkup(
      measurement,
      markup,
      startChanged || endChanged || middleChanged,
      (m, sourceViewer) => ({
        ...m,
        startPoint: updatedMeasurement.startPoint,
        endPoint: updatedMeasurement.endPoint,
        middlePoint: updatedMeasurement.middlePoint,
        value: updatedMeasurement.value,
        markups: this.getViewerMarkups(sourceViewer, m),
      }),
      'Angle measurement markup not found in viewers'
    );
  }

  private hasPicksChanged(
    measurementPicks: SnapPick[],
    markupPicks: SnapPick[]
  ): boolean {
    if (measurementPicks.length !== markupPicks.length) {
      return true;
    }
    return measurementPicks.some((p, index) => {
      const markupPick = markupPicks[index];
      const pPoint = p.point;
      const mPoint = markupPick.point;

      // If one has a point and the other doesn't, they're different
      if (!pPoint || !mPoint) {
        return pPoint !== mPoint;
      }

      return (
        pPoint.x !== mPoint.x || pPoint.y !== mPoint.y || pPoint.z !== mPoint.z
      );
    });
  }

  public setItemSavedStatus(
    id: string,
    savedStatus: MeasurementSaveStatus
  ): void {
    this.setItems((prev) => {
      return prev.map((item) => {
        if (item.id === id) return { ...item, savedStatus };
        return item;
      });
    });
  }

  private readonly handleMarkupModified =
    (
      viewerWrapper: Web3DViewerWrapper,
      previousViewerWrappers: Web3DViewerWrapper[] | undefined
    ) =>
    (e: Event) => {
      //wait for other synchronous function will be completed
      queueMicrotask(() => {
        const markup = (e as CustomEvent<Markup>).detail;
        const measurementUpdated = this.updateMeasurementForMarkup(markup);
        if (measurementUpdated) {
          return;
        }
        // Guard against stale replica events (NUMARK-797):
        //
        // When a user drags rapidly, multiple `markupmodified` events fire for
        // the source markup (e.g. B_pano) and queue microtasks A1, A2, ...
        // A1 creates replica markups on other viewers (D_3d, E_planar), which
        // each fire their own `markupmodified`, queuing microtasks B1, C1.
        // Because microtasks run FIFO, A2 executes before B1/C1. A2 calls
        // `removeById` on D_3d/E_planar and replaces them with fresh replicas.
        // When B1/C1 finally run, D_3d/E_planar are no longer in the plugin or
        // in `measurement.markups` — `updateMeasurementForMarkup` returns false
        // (not found), and without this guard the handler would treat the stale
        // replica as a brand-new user-drawn measurement, creating a phantom
        // duplicate visible in the panorama plane.
        //
        // The deeper cause is that `syncMeasurementWithMarkup` calls
        // `updateMeasurement` (setItems) with only the source markup, then pushes
        // replicas onto the array afterward — see the TODO on that method for the
        // proper long-term fix.
        const markupPlugin = viewerWrapper.markupPlugin;
        const isMarkupStillAlive = markupPlugin
          .getMarkups()
          .some((m) => m.id === markup.id);
        if (!isMarkupStillAlive) {
          return;
        }

        const label = getLabelFromMarkup(markup, this.items());
        const measurement = convertMarkupToMeasurement(
          markup,
          label,
          this.pointMeasurementSettings()
        );
        if (!measurement) {
          return;
        }

        // Apply last used style if available for this measurement type
        this.styleManager.applyLastUsedStyle(measurement, markup);

        // Apply chainage formatting to the 3D distance label for alignment measurements
        const alignSettings = this.alignmentSettings();
        if (measurement.type === 'alignment' && alignSettings) {
          this.applyChainageFormatterToMarkup(
            markup,
            alignSettings,
            this.formattingSettings()
          );
        }

        this.setItems((prevMeasurements) => {
          return [measurement, ...prevMeasurements];
        });

        // Create markups in other panes before setting selection, so the
        // selection effect can find markups in all viewers.
        if (previousViewerWrappers != undefined) {
          // Use measurement color if available (which includes last used style), otherwise use markup color
          const colorToUse = measurement.color
            ? rgbToWeb3dColor(measurement.color)
            : markup.color;
          this.createMeasurement(
            measurement,
            this.viewerWrappers().filter(
              (v) => v.viewer != viewerWrapper.viewer
            ),
            colorToUse,
            markup.origin
          );
        }

        this.setSelectedItemId(measurement.id);
      });
    };

  private readonly handleMarkupRemoved = (e: Event): void => {
    // wait for other synchronous function will be completed
    queueMicrotask(() => {
      const markup = (e as CustomEvent<Markup>).detail;
      const measurement = this.items().find((m) =>
        this.getValidMarkups(m.markups).some((m) => m.id == markup.id)
      );
      if (measurement) {
        if (measurement.type === 'volume') {
          // Boundary polygon is a display/selection proxy only; do not delete the volume.
          const volumeMeasurement = measurement as VolumeMeasurementType;
          this.updateMeasurement({
            ...volumeMeasurement,
            markups: this.getValidMarkups(volumeMeasurement.markups).filter(
              (m) => m.id !== markup.id
            ),
          });
          return;
        }
        this.removeMeasurement(measurement.id);
      }
    });
  };

  private readonly handleMarkupSelection =
    (viewerWrapper: Web3DViewerWrapper) =>
    (e: Event): void => {
      queueMicrotask(() => {
        this.processMarkupSelection(e as Web3DCustomEvent, viewerWrapper);
      });
    };

  private processMarkupSelection(
    selectionEvent: Web3DCustomEvent,
    viewerWrapper: Web3DViewerWrapper
  ): void {
    if (this.isMarkupSelectionSuppressed()) return;

    const selectedVolume = this.items()
      .filter((m) => m.type === 'volume')
      .find((m) => {
        const ids = viewerWrapper.viewer.selection.get(
          VOLUME_MEASUREMENT_TILESET_MODEL_ID_PREFIX + m.id
        );
        return ids != null && ids.length > 0;
      });

    if (selectedVolume) {
      this.setSelectedItemId(selectedVolume.id);
      return;
    }

    const isSingleSelection = selectionEvent.detail.count === 1;
    const selectedMarkupIds =
      viewerWrapper.viewer.selection.get('markup') || [];

    if (!isSingleSelection) {
      this.setSelectedItemId(null);
      return;
    }

    const [selectedMarkupId] = selectedMarkupIds;
    const selectedMeasurement = this.items().find((measurement) =>
      this.getValidMarkups(measurement.markups).some(
        (markup) => markup.id == selectedMarkupId
      )
    );

    if (selectedMeasurement) {
      this.setSelectedItemId(selectedMeasurement.id);
    } else {
      this.setSelectedItemId(null);
    }
  }

  private addEventListenersToViewer(
    viewerWrapper: Web3DViewerWrapper,
    previousViewers: Web3DViewerWrapper[] | undefined
  ): void {
    const markupModifiedHandler = this.handleMarkupModified(
      viewerWrapper,
      previousViewers
    );
    const markupRemovedHandler = this.handleMarkupRemoved;
    const markupSelectionHandler = this.handleMarkupSelection(viewerWrapper);

    viewerWrapper.viewer.addEventListener(
      'markupmodified' as MarkupEventName.Modified,
      markupModifiedHandler
    );
    viewerWrapper.viewer.addEventListener(
      'markupremoved' as MarkupEventName.Removed,
      markupRemovedHandler
    );
    viewerWrapper.viewer.addEventListener('selection', markupSelectionHandler);
    this.viewerEventListeners.set(viewerWrapper.viewer, [
      { event: 'markupmodified', handler: markupModifiedHandler },
      { event: 'markupremoved', handler: markupRemovedHandler },
      { event: 'selection', handler: markupSelectionHandler },
    ]);
  }

  private removeEventListenersForViewer(
    viewerWrapper: Web3DViewerWrapper
  ): void {
    const listeners = this.viewerEventListeners.get(viewerWrapper.viewer);
    if (listeners) {
      listeners.forEach(({ event, handler }) => {
        viewerWrapper.viewer.removeEventListener(event, handler);
      });
      this.viewerEventListeners.delete(viewerWrapper.viewer);
    }
  }

  private removeAllEventListeners(): void {
    this.viewerEventListeners.forEach((listeners, viewer) => {
      listeners.forEach(({ event, handler }) => {
        viewer.removeEventListener(event, handler);
      });
    });
    this.viewerEventListeners.clear();
  }

  public removeMeasurement(measurementId: string): void {
    if (this.selectedItemId() === measurementId) {
      this.setSelectedItemId(null);
    }
    this.setItems((prev) => prev.filter((m) => m.id !== measurementId));
  }

  public removeAllMeasurements(): void {
    this.setSelectedItemId(null);
    this.setItems([]);
  }

  public toggleMeasurementVisibility(measurement: Measurement): void {
    measurement.hidden = !measurement.hidden;
    this.getValidMarkups(measurement.markups).forEach((markup) => {
      this.setMarkupVisibility(markup, !measurement.hidden);
    });
    this.viewerWrappers().forEach((wrapper) => {
      wrapper.viewer.dispatchEvent(new Event('markupvisibilitychanged'));
      wrapper.viewer.waitForRender();
    });
    if (measurement.hidden && this.selectedItemId() === measurement.id) {
      this.setSelectedItemId(null);
    }
    this.updateMeasurement(measurement);
  }

  /**
   * Handles visibility for all measurements agnostic of type
   *
   * @param visible
   */
  public setAllMeasurementsVisibility(visible: boolean): void {
    const currentItems = this.items();
    const newItems = currentItems.map((item) => {
      const updatedItem = {
        ...item,
        hidden: !visible,
      };
      this.getValidMarkups(updatedItem.markups).forEach((markup) => {
        this.setMarkupVisibility(markup, visible && !updatedItem.hidden);
      });
      this.styleManager.updateLastUsedStyle(updatedItem, item);
      return updatedItem;
    });
    this.setItems(newItems);

    if (!visible && this.selectedItemId() !== null) {
      this.setSelectedItemId(null);
    }
  }

  private clearMarkupSelection(): void {
    for (const vw of this.viewerWrappers()) {
      vw.viewer.selection.clear('markup');
    }
  }

  private setMarkupRenderOrder(markup: Markup, order: number): void {
    markup.traverse((child) => {
      child.renderOrder = order;
    });
  }

  private setMarkupVisibility(markup: Markup, visible: boolean): void {
    markup.visible = visible;
    markup.traverse((child) => {
      child.visible = visible;
    });
  }

  private bringMarkupsToFront(markups: Markup[]): void {
    for (const markup of markups) {
      this.setMarkupRenderOrder(markup, SELECTED_MARKUP_RENDER_ORDER);
    }
  }

  private resetAllMarkupsRenderOrder(): void {
    for (const measurement of this.items()) {
      for (const markup of this.getValidMarkups(measurement.markups)) {
        this.setMarkupRenderOrder(markup, DEFAULT_MARKUP_RENDER_ORDER);
      }
    }
  }

  public updateMarkupColor(color: string): void {
    for (const vw of this.viewerWrappers()) {
      vw.viewer.plugins.get(MarkupPlugin)!.settings.color = new Color(color);
    }
  }

  public getLastUsedColorForType(type: MeasurementType): RGBFormat | undefined {
    return this.styleManager.getLastUsedColor(type);
  }

  public setLastUsedColorForType(
    type: MeasurementType,
    color: RGBFormat
  ): void {
    this.styleManager.setLastUsedColor(type, color);
  }

  public enableMarkupDelete(enableDelete: boolean): void {
    for (const vw of this.viewerWrappers()) {
      vw.viewer.plugins.get(MarkupPlugin)!.settings.deleteEnabled =
        enableDelete;
    }
  }

  public loadMeasurements(measurements: Measurement[]): void {
    this.removeAllMeasurements();

    for (const measurement of measurements) {
      this.createMeasurement(measurement);
    }

    // Preserve API order for loaded measurements.
    this.setItems([...measurements]);
  }

  public addMeasurement(measurement: Measurement): boolean {
    // For volume measurements, create the boundary polygon markup before adding
    // to the store (mirroring the loadMeasurements pattern) so the boundary is
    // rendered immediately after save without requiring a page reload.
    if (measurement.type === 'volume') {
      this.createMeasurement(measurement);
    }
    return this.addItem(measurement);
  }

  /**
   * Restores measurements (e.g. alignment) from reserved view data alongside
   * native Connect markups, appending to the store without clearing existing
   * ones (NUMARK-968). On a mid-way `createMeasurement` failure, partial markups
   * are removed before rethrowing so cleanup (driven by `items()`) isn't bypassed.
   */
  public restoreMeasurements(measurements: Measurement[]): void {
    if (measurements.length === 0) return;

    const created: Measurement[] = [];
    try {
      for (const measurement of measurements) {
        try {
          this.createMeasurement(measurement);
          // createMeasurement renders markups visible; re-apply persisted hidden state.
          if (measurement.hidden) {
            this.getValidMarkups(measurement.markups).forEach((markup) =>
              this.setMarkupVisibility(markup, false)
            );
          }
          created.push(measurement);
        } catch (error) {
          this.removeMeasurementMarkupsFromViewers(measurement);
          throw error;
        }
      }
    } finally {
      if (created.length > 0) {
        this.setItems((prev) => [...prev, ...created]);
      }
    }
  }

  /** Removes a measurement's markups from every viewer to undo a partial restore. */
  private removeMeasurementMarkupsFromViewers(measurement: Measurement): void {
    const ids = this.getValidMarkups(measurement.markups).map((m) => m.id);
    if (ids.length === 0) return;

    for (const viewerWrapper of this.viewerWrappers()) {
      const markupPlugin = viewerWrapper.viewer.plugins.get(MarkupPlugin);
      if (!markupPlugin) continue;

      const markupIds = markupPlugin
        .getMarkups()
        .filter((m) => ids.some((id) => id === m.id))
        .map((m) => m.id);

      if (markupIds.length) {
        markupPlugin.removeById(markupIds);
      }
    }
  }

  // TODO: Add support for slope measurement type once its available in Numark
  public loadMeasurementsForConnectView(markups: ConnectMarkup[]): void {
    this.removeAllMeasurements();

    // Transform API data to measurements
    const measurements = markups
      .map((markup) => processMarkup(markup))
      .filter((markup): markup is MeasurementMarkup => markup !== null)
      .map((markup) => transformTcMarkupToMeasurement(markup))
      .filter((m): m is Measurement => m !== null);

    measurements.forEach((measurement) => {
      this.createMeasurement(measurement);
    });

    // Preserve API order from Connect markup payload.
    this.setItems([...measurements]);
  }

  private requirePolygonMeasurementTool(
    vw: Web3DViewerWrapper
  ): PolygonMeasurementTool {
    const polygonMeasurementTool = vw.viewer.tools.get(PolygonMeasurementTool);
    if (!polygonMeasurementTool) {
      throw new Error('PolygonMeasurementTool is not available on the viewer');
    }
    return polygonMeasurementTool;
  }

  private createMeasurement(
    measurement: Measurement,
    targetViewerWrappers: Web3DViewerWrapper[] = this.viewerWrappers(),
    color?: Color,
    origin?: unknown
  ): void {
    // Use color from measurement if available, otherwise use provided color or default
    const measurementColor = measurement.color
      ? new Color(
          measurement.color.r / RGB_FACTOR,
          measurement.color.g / RGB_FACTOR,
          measurement.color.b / RGB_FACTOR
        )
      : color || DEFAULT_MAGENTA_COLOR;
    for (const vw of targetViewerWrappers) {
      switch (measurement.type) {
        case 'line': {
          const measurementTool = vw.viewer.tools.get(MeasurementTool);
          if (!measurementTool) {
            throw new Error('MeasurementTool is not available on the viewer');
          }
          const lineMarkup = measurementTool.add(
            {
              snapType: SnapType.POINT as number,
              point: measurement.startPoint,
            },
            {
              snapType: SnapType.POINT as number,
              point: measurement.endPoint,
            },
            measurementColor,
            origin
          );
          measurement.markups.push(lineMarkup);
          break;
        }
        case 'point': {
          const pointMarkupTool = vw.viewer.tools.get(PointMarkupTool);
          if (!pointMarkupTool) {
            throw new Error('PointMarkupTool is not available on the viewer');
          }
          const pointMarkup = pointMarkupTool.add(
            {
              point: partialPointToVector3(measurement),
            },
            measurementColor,
            origin
          );
          queueMicrotask(() => {
            pointMarkup.imageUrl = measurement.pointSymbol?.url || '';
            pointMarkup.imagePosition =
              measurement.pointSymbol?.position || ImagePosition.Center;
            pointMarkup.positionFormatter = (position) =>
              pointPositionFormatter(
                position,
                vw.viewer,
                measurement.showXYCoordinates,
                measurement.showZCoordinate,
                measurement.label
              );
          });
          measurement.markups.push(pointMarkup);
          break;
        }
        case 'angle': {
          const aTool = vw.viewer.tools.get(AngleMeasurementTool);
          if (!aTool) {
            throw new Error(
              'AngleMeasurementTool is not available on the viewer'
            );
          }
          const prevSnapTypes = [...vw.viewer.settings.snapTypes];
          vw.viewer.settings.snapTypes = [SnapType.POINT as number];
          const angleMarkup = aTool.add(
            {
              snapType: SnapType.POINT as number,
              point: measurement.startPoint,
            },
            {
              snapType: SnapType.POINT as number,
              point: measurement.endPoint,
            },
            {
              snapType: SnapType.POINT as number,
              point: measurement.middlePoint,
            },
            measurementColor,
            origin
          );
          measurement.markups.push(angleMarkup);
          vw.viewer.settings.snapTypes = prevSnapTypes;
          break;
        }
        case 'alignment': {
          const alignmentTool = vw.viewer.tools.get(AlignmentMeasurementTool);
          if (!alignmentTool) {
            throw new Error(
              'AlignmentMeasurementTool is not available on the viewer'
            );
          }
          const prevAlignSnapTypes = [...vw.viewer.settings.snapTypes];
          vw.viewer.settings.snapTypes = [SnapType.POINT as number];
          const alignmentMarkup = alignmentTool.add(
            {
              snapType: SnapType.POINT as number,
              point: measurement.ground,
            },
            {
              snapType: SnapType.POINT as number,
              point: measurement.point,
            },
            {
              snapType: SnapType.POINT as number,
              point: measurement.alignment,
            },
            measurement.value,
            measurementColor,
            origin
          );
          const alignSettingsForMarkup = this.alignmentSettings();
          if (alignSettingsForMarkup) {
            this.applyChainageFormatterToMarkup(
              alignmentMarkup,
              alignSettingsForMarkup,
              this.formattingSettings()
            );
          }
          measurement.markups.push(alignmentMarkup);
          vw.viewer.settings.snapTypes = prevAlignSnapTypes;
          break;
        }
        case 'polyline': {
          const polylineMeasurementTool = vw.viewer.tools.get(
            PolylineMeasurementTool
          );
          if (!polylineMeasurementTool) {
            throw new Error(
              'PolylineMeasurementTool is not available on the viewer'
            );
          }
          const polylineMarkup = polylineMeasurementTool.add(
            measurement.picks,
            measurementColor,
            origin
          );
          polylineMarkup.showSegmentDistances =
            measurement.showSegmentDistances;
          polylineMarkup.showSegmentSlopes = measurement.showSegmentSlopes;
          polylineMarkup.showHorizontalMeasurements =
            measurement.showHorizontalMeasurements;
          polylineMarkup.showVerticalMeasurements =
            measurement.showVerticalMeasurements;
          polylineMarkup.slopeFormatter = (vertical, horizontal) =>
            slopeFormatter(
              vertical,
              horizontal,
              measurement.slopeFormat || this.slopeFormat()
            );
          measurement.markups.push(polylineMarkup);
          break;
        }
        case 'area': {
          const polygonMeasurementTool = this.requirePolygonMeasurementTool(vw);
          // Clone picks before passing to the tool. web3d stores references to
          // the Vector3 objects it receives and mutates them in-place when the
          // user drags a handle (same issue documented in createLineMeasurement).
          // By handing the tool independent clones, measurement.picks retains
          // its own Vector3 instances so that hasPicksChanged() can detect
          // position changes correctly.
          const picksForMarkup = clonePicks(measurement.picks);
          const polygonMarkup = polygonMeasurementTool.add(
            picksForMarkup,
            measurementColor,
            origin
          );
          polygonMarkup.fillOpacity = measurement.fillOpacity;
          polygonMarkup.lineStyle = measurement.lineStyle;
          polygonMarkup.lineThickness = measurement.lineThickness;
          measurement.markups.push(polygonMarkup);
          break;
        }
        case 'volume': {
          const volumeMeasurement = measurement as VolumeMeasurementType;
          const picks = boundaryInclusiveToSnapPicks(
            volumeMeasurement.parameters.boundaryInclusive
          );
          if (!picks) {
            break;
          }
          const polygonMeasurementTool = this.requirePolygonMeasurementTool(vw);
          // polygonMeasurementTool.add() expects a closed polygon (last pick == first
          // pick). boundaryInclusiveToSnapPicks strips the GeoJSON closing duplicate,
          // so we re-add it here; without it the tool silently drops the last vertex.
          // Clone the closing pick's point so the tool cannot corrupt the first
          // vertex via in-place Vector3 mutation (same defence as clonePicks).
          const closedPicks = [
            ...picks,
            { ...picks[0], point: picks[0].point?.clone() },
          ];
          const polygonMarkup = polygonMeasurementTool.add(
            closedPicks,
            measurementColor,
            origin
          );
          // Volume boundary is display/selection only; do not allow vertex drag like area measurements.
          polygonMarkup.editable = false;
          polygonMarkup.fillOpacity =
            DEFAULT_POLYGON_MEASUREMENT_SETTINGS.fillOpacity;
          polygonMarkup.lineStyle =
            DEFAULT_POLYGON_MEASUREMENT_SETTINGS.lineStyle;
          polygonMarkup.lineThickness =
            DEFAULT_POLYGON_MEASUREMENT_SETTINGS.lineThickness;
          volumeMeasurement.markups.push(polygonMarkup);
          break;
        }
        default:
          throw new UnreachableCaseError(measurement);
      }
    }
  }

  public getBoundaryPointsFromPolygonMarkup = (
    pickId: number | string
  ): Vector3[] | undefined =>
    getBoundaryPointsFromPolygonMarkup(this.items(), pickId);

  public updateMeasurement(updatedMeasurement: Measurement): void {
    const items = this.items();
    let currentMeasurement: Measurement | undefined;
    let mergedMeasurement: Measurement | undefined;
    const newItems = items.map((item) => {
      if (item.id === updatedMeasurement.id) {
        currentMeasurement = item;
        const merged = { ...item, ...updatedMeasurement };
        mergedMeasurement = merged;
        return merged;
      }
      return item;
    });

    // For volume measurements, determine whether the boundary geometry changed
    // (which requires a full markup remove/recreate) or only display properties
    // changed (name, color, visibility) which can be applied in-place without
    // triggering selection events that would close the panel.
    if (updatedMeasurement.type === 'volume' && mergedMeasurement) {
      const volumeCurrent = currentMeasurement as VolumeMeasurementType;
      const volumeUpdated =
        updatedMeasurement as Partial<VolumeMeasurementType>;

      const applyToExistingMarkups = (
        fn: (existingMarkups: Markup[], vw: Web3DViewerWrapper) => void
      ): void => {
        for (const vw of this.viewerWrappers()) {
          const existingMarkups = this.getViewerMarkups(
            vw.viewer,
            currentMeasurement!
          );
          fn(existingMarkups, vw);
        }
      };

      const updateExistingMarkupsProperty = (
        property: 'color',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: any
      ): void => {
        applyToExistingMarkups((existingMarkups) => {
          for (const markup of existingMarkups) {
            (markup as Markup)[property] = value;
          }
        });
      };

      const updateExistingMarkupsVisibility = (visible: boolean): void => {
        applyToExistingMarkups((existingMarkups) => {
          for (const markup of existingMarkups) {
            this.setMarkupVisibility(markup, visible);
          }
        });
      };

      const boundaryChanged =
        volumeUpdated.parameters?.boundaryInclusive !== undefined &&
        volumeUpdated.parameters.boundaryInclusive !==
          volumeCurrent.parameters?.boundaryInclusive;

      if (boundaryChanged) {
        // // Boundary geometry changed — must remove and recreate markups.
        applyToExistingMarkups((existingMarkups, vw) => {
          if (existingMarkups.length > 0) {
            vw.markupPlugin.removeById(existingMarkups.map((m) => m.id));
          }
        });
        mergedMeasurement.markups = [];

        this.createMeasurement(mergedMeasurement);
        // createMeasurement() always adds markups as visible, so respect the
        // measurement's stored visibility state.
        const isHidden =
          updatedMeasurement.hidden ?? currentMeasurement?.hidden ?? false;
        if (isHidden) {
          this.getValidMarkups(mergedMeasurement.markups).forEach((markup) => {
            this.setMarkupVisibility(markup, false);
          });
        }
      } else {
        const hasStylePropertyUpdate =
          updatedMeasurement.color !== undefined ||
          updatedMeasurement.hidden !== undefined;

        if (hasStylePropertyUpdate) {
          // The merge may have overwritten markups with a stale array from
          // updatedMeasurement (e.g. { ...m, color } captured before a prior
          // boundary edit replaced markups). Restore the live store markups.
          mergedMeasurement.markups = currentMeasurement!.markups;
        }

        // Property-only change (color, visibility) — update existing
        // markups in-place to avoid triggering selection events.
        if (updatedMeasurement.color) {
          const newColor = rgbToWeb3dColor(updatedMeasurement.color);
          updateExistingMarkupsProperty('color', newColor);
        }
        if (updatedMeasurement.hidden !== undefined) {
          updateExistingMarkupsVisibility(!updatedMeasurement.hidden);
        }
      }
    }

    this.setItems(newItems);

    // Save last used style for this measurement type when color or symbol changes
    if (currentMeasurement) {
      this.styleManager.updateLastUsedStyle(
        updatedMeasurement,
        currentMeasurement
      );
    }
  }

  public getViewerMarkups(
    viewer: Web3DViewer,
    measurement: Measurement
  ): Markup[] {
    const markupPlugin = viewer.plugins.get(MarkupPlugin);
    if (!markupPlugin) {
      throw new Error('MarkupPlugin is not available on the viewer');
    }
    return markupPlugin
      .getMarkups()
      .filter((m) =>
        this.getValidMarkups(measurement.markups).some((mp) => mp.id === m.id)
      );
  }
}
